import { ConfirmationModal, CustomButton, CustomDrawer } from '@components';
import { ClientOrganization, Invoice, OrganizationBilling, RefundReason, RefundType } from '@graphql';
import { useMemo, useState } from 'react';
import { COLORS } from '@styles';
import { Select, FormControl, MenuItem, InputLabel, TextField, InputAdornment } from '@mui/material';
import { camel2Title, formatMoney } from '@utils';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import AttachMoney from '@mui/icons-material/AttachMoney';
import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import { OrganizationInfo } from '../OrganizationInfo';
import { InvoiceDetails } from '../InvoiceDetails';

interface IInvoiceRefundDrawerProps {
  open: boolean;
  onClose: () => void;
  organization?: ClientOrganization;
  invoice?: Invoice | null;
  organizationBilling?: OrganizationBilling;
}

const styles = {
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '85vh',
  } as React.CSSProperties,
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'none',
    padding: '0 1rem',
    gap: '1.5rem',
  } as React.CSSProperties,
  formItems: {
    borderRadius: '1rem',
  },
  inputBorder: {
    borderRadius: '1rem',
  },
  button: {
    padding: '1.5rem 1rem 0 1rem',
    borderTop: `1px solid ${COLORS.NEUTRAL_DARK_2}`,
    marginTop: 'auto',
  },
  creditCardContainer: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'space-between',
    alignItems: 'center',
  } as React.CSSProperties,
  creditCardContentContainer: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-start',
    alignItems: 'center',
  } as React.CSSProperties,
  creditCardIconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0.5625rem',
    borderRadius: '1.5rem',
    backgroundColor: COLORS.NEUTRAL_DARK_2,
    color: COLORS.NEUTRAL_LIGHT_0,
  } as React.CSSProperties,
  creditCardItem: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  } as React.CSSProperties,
  creditCardNumber: {
    fontSize: '0.875rem',
    fontWeight: 600,
  } as React.CSSProperties,
  creditCardFailedReason: {
    fontSize: '0.75rem',
  } as React.CSSProperties,
  creditCardAttemptCount: {
    fontSize: '0.75rem',
    color: COLORS.NEUTRAL_LIGHT_0,
  } as React.CSSProperties,
}

export function InvoiceRefundDrawer({ open, onClose, organization, invoice, organizationBilling }: IInvoiceRefundDrawerProps) {
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);

  const handleCloseConfirmationModal = () => setIsOpenConfirmationModal(false);
  const handleOpenConfirmationModal = () => setIsOpenConfirmationModal(true);

  const initialValues = useMemo(
    () => ({
      id: '',
      refundAmount: 0,
      refundReason: RefundReason.RequestedByCustomer,
      refundType: RefundType.Full,
      description: '',
    }),
    [],
  );

  const validationSchema = Yup.object().shape({
    refundAmount: Yup.number().when(['refundType'], {
      is: (refundType: RefundType) => refundType === RefundType.Partial,
      then: (schema: Yup.NumberSchema) => schema.required('Refund amount is required.')
        .min(0, 'Refund amount must be greater than 0.')
        .max(invoice?.amountPaid || 0, 'Refund amount must be less than the invoice amount.'),
      otherwise: (schema: Yup.NumberSchema) => schema,
    }),
    refundReason: Yup.string().required('Refund reason is required.'),
    description: Yup.string().when(['refundReason'], {
      is: (refundReason: RefundReason) => refundReason === RefundReason.Other,
      then: (schema: Yup.StringSchema) => schema.required('Description is required'),
      otherwise: (schema: Yup.StringSchema) => schema,
    }),
  });

  const { handleSubmit, handleChange, values, errors, isSubmitting, setFieldValue } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (payload) => {
      if (payload.refundType === RefundType.Full) {
        setFieldValue('refundAmount', invoice?.amountPaid || 0);
      }

      handleOpenConfirmationModal();
    },
  });

  const refundTypeSelectOptions = useMemo(
    () => [
      { label: `Full Refund (${formatMoney(invoice?.amountPaid || 0)} CAD)`, value: RefundType.Full },
      { label: `Partial Refund`, value: RefundType.Partial },
    ],
    [invoice?.amountPaid],
  );

  const refundReasonSelectOptions = useMemo(
    () => Object.values(RefundReason).map((reason) => ({ label: camel2Title(reason), value: reason })),
    [],
  );

  // TODO: Add API call to apply credit
  const onConfirm = async () => {
    handleCloseConfirmationModal();
    onClose();
  };

  return (
    <>
      <CustomDrawer
        open={open}
        onClose={onClose}
        headerLabel="Refund Payment"
        headerIcon={<RequestQuoteOutlined />}
      >
        <form style={styles.formWrapper} onSubmit={handleSubmit}>
          <div style={styles.formContainer}>
            <OrganizationInfo organization={organization} />
            <InvoiceDetails
              invoice={invoice}
              organizationBilling={organizationBilling}
              amountField="amountPaid"
            />

            <FormControl fullWidth>
              <InputLabel sx={styles.formItems}>Refund Type</InputLabel>
              <Select
                sx={styles.formItems}
                fullWidth
                name="refundType"
                onChange={handleChange}
                value={values.refundType}
                label="Refund Type"
              >
                {refundTypeSelectOptions.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {values.refundType === RefundType.Partial && (
              <TextField
                sx={styles.formItems}
                fullWidth
                name="refundAmount"
                onChange={handleChange}
                value={values.refundAmount}
                label="Refund Amount"
                required
                error={!!errors.refundAmount}
                helperText={errors.refundAmount}
                InputProps={{
                  style: styles.inputBorder,
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <FormControl fullWidth>
              <InputLabel sx={styles.formItems}>Refund Reason</InputLabel>
              <Select
                sx={styles.formItems}
                fullWidth
                name="refundReason"
                onChange={handleChange}
                value={values.refundReason}
                label="Refund Reason"
              >
                {refundReasonSelectOptions.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              sx={styles.formItems}
              fullWidth
              name="description"
              onChange={handleChange}
              value={values.description}
              label="Description"
              variant="outlined"
              required={values.refundReason === RefundReason.Other}
              multiline
              minRows={4}
              error={!!errors.description}
              helperText={errors.description}
              InputProps={{ style: styles.inputBorder }}
            />

            <div style={styles.button}>
              <CustomButton variant="default" type="submit" isLoading={isSubmitting}>
                Process Refund
              </CustomButton>
            </div>
          </div>
        </form>
      </CustomDrawer>
      <ConfirmationModal
        icon={<RequestQuoteOutlined />}
        title={`Refund ${formatMoney(values?.refundAmount || 0)} to ${organization?.name}?`}
        onClose={handleCloseConfirmationModal}
        isOpen={isOpenConfirmationModal}
        variant="default"
        onConfirm={onConfirm}
        confirmationButtonLabel="Confirm Refund"
        confirmationText={organization?.name || ''}
      >
        <p>
          This refund cannot be undone and will take up to 10 business days to appear in their account.
        </p>
        <p>Type the organization name to confirm:</p>
        <strong>{organization?.name || ''}</strong>
      </ConfirmationModal>
    </>
  );
}
